import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue
} from '@/components/ui/select';
import {
    Building2,
    User as UserIcon,
    CreditCard,
    Layers,
    Settings,
    CheckCircle2,
    ArrowRight,
    ArrowLeft,
    Loader2,
    Mail,
    Lock,
    Phone,
    Globe,
    MapPin,
    Clock,
    ShieldCheck,
    Check
} from 'lucide-react';
import { PlanType, SaasTier, MembershipPlan } from '@/types';
import { cn } from '@/lib/utils';
import { adminApi, gymApi } from '@/lib/api';
import { useToast } from '@/hooks/use-toast';
import { ALL_MODULES } from '@/lib/constants';
import { supabase } from '@/lib/supabase';
import * as Icons from 'lucide-react';

interface CreateGymWizardProps {
    onSuccess: () => void;
    onCancel: () => void;
}

const STEPS = [
    { id: 1, title: 'Datos Básicos', icon: Icons.Building2 },
    { id: 2, title: 'Admin del Gym', icon: Icons.User },
    { id: 3, title: 'Plan y Facturación', icon: Icons.CreditCard },
    { id: 4, title: 'Módulos', icon: Icons.Layers },
    { id: 5, title: 'Configuración', icon: Icons.Settings },
    { id: 6, title: 'Confirmar', icon: Icons.CheckCircle2 },
];

export function CreateGymWizard({ onSuccess, onCancel }: CreateGymWizardProps) {
    const [currentStep, setCurrentStep] = useState(1);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [saasTiers, setSaasTiers] = useState<SaasTier[]>([]);
    const { toast } = useToast();

    useEffect(() => {
        const fetchTiers = async () => {
            try {
                const { data, error } = await supabase
                    .from('saas_tiers')
                    .select('*')
                    .order('price', { ascending: true });
                if (data) {
                    setSaasTiers(data);
                    // Comportamiento esperado: los gyms nuevos nacen como trial Elite
                    // (todo incluido excepto WhatsApp/Webhooks).
                    const eliteTier = data.find((t: SaasTier) =>
                        t.name.toLowerCase().includes('elite') && !t.is_private
                    );
                    const defaultTier = eliteTier || data[0];
                    if (defaultTier) {
                        setFormData(prev => ({
                            ...prev,
                            plan: defaultTier.name,
                            tier_id: defaultTier.id,
                            price: defaultTier.price.toString(),
                            modules: defaultTier.included_modules || prev.modules,
                        }));
                    }
                }
            } catch (e) {
                console.error(e);
            }
        };
        fetchTiers();
    }, []);

    const [formData, setFormData] = useState({
        // Step 1
        name: '',
        country: 'México',
        city: '',
        timezone: 'America/Mexico_City',
        phone: '',
        email: '',
        // Step 2
        adminName: '',
        adminEmail: '',
        adminPassword: '',
        sendEmail: true,
        // Step 3
        plan: 'Elite' as PlanType,
        tier_id: '',
        price: '0',
        billingCycle: 'monthly',
        expiryDate: '',
        status: 'active',
        isTrial: true,
        // Step 4
        modules: ['clients', 'memberships', 'scanner'],
        // Step 5
        openingHours: '06:00',
        closingHours: '22:00',
        multipleCheckin: false,
        attendanceMethod: 'qr',
        maxCapacity: '50'
    });

    const updateField = (field: string, value: any) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const calculateTotalPrice = () => {
        const selectedTier = saasTiers.find(t => t.id === formData.tier_id || t.name === formData.plan);
        const basePrice = selectedTier ? selectedTier.price : 0;

        // If we have a SaaS tier, the modules it includes are already in the basePrice.
        const includedModules = selectedTier?.included_modules || [];

        const extraModulesPrice = formData.modules.reduce((sum, modId) => {
            // If the module is already included in the base plan price, don't charge extra
            if (includedModules.includes(modId)) return sum;

            const mod = ALL_MODULES.find(m => m.module_id === modId);
            return sum + (mod?.price || 0);
        }, 0);

        return basePrice + extraModulesPrice;
    };

    const selectedTier = saasTiers.find(t => t.id === formData.tier_id || t.name === formData.plan);

    // Recalcula el precio según el tier y los módulos (mantiene el precio manual si lo editan)
    useEffect(() => {
        const total = calculateTotalPrice();
        updateField('price', total.toString());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formData.tier_id, formData.modules]);

    const handleNext = () => {
        if (currentStep < 6) setCurrentStep(currentStep + 1);
    };

    const handleBack = () => {
        if (currentStep > 1) setCurrentStep(currentStep - 1);
    };

    const handleSubmit = async () => {
        setIsSubmitting(true);
        try {
            const apiPayload = {
                name: formData.name,
                country: formData.country,
                city: formData.city,
                timezone: formData.timezone,
                phone: formData.phone,
                email: formData.email,
                plan: formData.plan,
                tier_id: formData.tier_id,
                price: parseFloat(formData.price),
                billing_cycle: formData.billingCycle,
                expiry_date: formData.expiryDate,
                status: formData.status,
                is_trial: formData.isTrial,
                modules_enabled: formData.modules,
                settings: {
                    opening_hours: formData.openingHours,
                    closing_hours: formData.closingHours,
                    multiple_checkin: formData.multipleCheckin,
                    attendance_method: formData.attendanceMethod,
                    max_capacity: parseInt(formData.maxCapacity),
                    device_binding_enabled: false
                },
                admin: {
                    name: formData.adminName,
                    email: formData.adminEmail,
                    password: formData.adminPassword
                }
            };

            const res = await adminApi.createGym(apiPayload);
            if (res.success) {
                toast({
                    title: "¡Éxito!",
                    description: `Gimnasio ${formData.name} creado correctamente.`,
                });
                onSuccess();
            } else {
                toast({
                    title: "Aviso",
                    description: res.warning || "Error al crear gimnasio",
                    variant: res.warning ? "default" : "destructive",
                });
            }
        } catch (error: any) {
            toast({
                title: "Error",
                description: error.message || "Error inesperado al crear gimnasio",
                variant: "destructive",
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const isStepValid = () => {
        switch (currentStep) {
            case 1: return formData.name && formData.email;
            case 2: return formData.adminName && formData.adminEmail && formData.adminPassword.length >= 6;
            case 3: return true;
            case 4: return formData.modules.length > 0;
            default: return true;
        }
    };

    return (
        <div className="flex flex-col gap-6">
            {/* Step Progress */}
            <div className="flex justify-between mb-8 px-2">
                {STEPS.map((step) => {
                    const Icon = step.icon;
                    const isActive = currentStep === step.id;
                    const isCompleted = currentStep > step.id;

                    return (
                        <div key={step.id} className="flex flex-col items-center gap-2 flex-1 relative">
                            <div className={cn(
                                "w-10 h-10 rounded-full flex items-center justify-center transition-all border-2",
                                isActive ? "bg-primary border-primary text-primary-foreground shadow-lg scale-110" :
                                    isCompleted ? "bg-green-500 border-green-500 text-white" :
                                        "bg-muted border-muted text-muted-foreground"
                            )}>
                                {isCompleted ? <Check className="h-5 w-5" /> : <Icon className="h-5 w-5" />}
                            </div>
                            <span className={cn(
                                "text-[10px] font-bold uppercase tracking-tight text-center hidden md:block",
                                isActive ? "text-primary" : "text-muted-foreground"
                            )}>
                                {step.title}
                            </span>
                            {/* Connector Line */}
                            {step.id < 6 && (
                                <div className="absolute top-5 left-[calc(50%+20px)] w-[calc(100%-40px)] h-[2px] bg-muted -z-10">
                                    <div className={cn(
                                        "h-full bg-green-500 transition-all duration-300",
                                        isCompleted ? "w-full" : "w-0"
                                    )} />
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            {/* Step Content */}
            <div className="flex-1 px-4 py-2">
                {currentStep === 1 && (
                    <div className="space-y-4 animate-in fade-in slide-in-from-right-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>Nombre del Gimnasio *</Label>
                                <div className="relative">
                                    <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                    <Input
                                        placeholder="Ej: Iron Forge Gym"
                                        className="pl-10"
                                        value={formData.name}
                                        onChange={(e) => updateField('name', e.target.value)}
                                    />
                                </div>
                            </div>
                            <div className="space-y-2">
                                <Label>Email de contacto *</Label>
                                <div className="relative">
                                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                    <Input
                                        type="email"
                                        placeholder="contacto@gym.com"
                                        className="pl-10"
                                        value={formData.email}
                                        onChange={(e) => updateField('email', e.target.value)}
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>País</Label>
                                <div className="relative">
                                    <Globe className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                    <Input
                                        placeholder="México"
                                        className="pl-10"
                                        value={formData.country}
                                        onChange={(e) => updateField('country', e.target.value)}
                                    />
                                </div>
                            </div>
                            <div className="space-y-2">
                                <Label>Ciudad</Label>
                                <div className="relative">
                                    <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                    <Input
                                        placeholder="CDMX"
                                        className="pl-10"
                                        value={formData.city}
                                        onChange={(e) => updateField('city', e.target.value)}
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>Teléfono / WhatsApp</Label>
                                <div className="relative">
                                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                    <Input
                                        placeholder="+52 55..."
                                        className="pl-10"
                                        value={formData.phone}
                                        onChange={(e) => updateField('phone', e.target.value)}
                                    />
                                </div>
                            </div>
                            <div className="space-y-2">
                                <Label>Zona Horaria</Label>
                                <Select value={formData.timezone} onValueChange={(v) => updateField('timezone', v)}>
                                    <SelectTrigger>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="America/Mexico_City">México (CDMX / Central)</SelectItem>
                                        <SelectItem value="America/Monterrey">México (Monterrey)</SelectItem>
                                        <SelectItem value="America/Tijuana">México (Tijuana)</SelectItem>
                                        <SelectItem value="America/Bogota">Colombia / Perú / Ecuador</SelectItem>
                                        <SelectItem value="America/Santiago">Chile (Santiago)</SelectItem>
                                        <SelectItem value="America/Argentina/Buenos_Aires">Argentina (B. Aires)</SelectItem>
                                        <SelectItem value="America/Caracas">Venezuela (Caracas)</SelectItem>
                                        <SelectItem value="America/Guatemala">Centroamérica (GMT-6)</SelectItem>
                                        <SelectItem value="America/Panama">Panamá</SelectItem>
                                        <SelectItem value="America/Santo_Domingo">Rep. Dominicana / PR</SelectItem>
                                        <SelectItem value="Europe/Madrid">España (Madrid)</SelectItem>
                                        <SelectItem value="America/New_York">USA (Eastern Time)</SelectItem>
                                        <SelectItem value="America/Chicago">USA (Central Time)</SelectItem>
                                        <SelectItem value="America/Los_Angeles">USA (Pacific Time)</SelectItem>
                                        <SelectItem value="UTC">UTC (Universal)</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                    </div>
                )}

                {currentStep === 2 && (
                    <div className="space-y-6 animate-in fade-in slide-in-from-right-4">
                        <div className="bg-primary/5 p-4 rounded-lg border border-primary/10 mb-4">
                            <p className="text-sm text-primary font-medium">
                                Define la cuenta maestra para este gimnasio. Se le asignará el rol de <b>Gym Admin</b>.
                            </p>
                        </div>
                        <div className="space-y-4">
                            <div className="space-y-2">
                                <Label>Nombre Completo *</Label>
                                <div className="relative">
                                    <UserIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                    <Input
                                        placeholder="Admin del Gimnasio"
                                        className="pl-10"
                                        value={formData.adminName}
                                        onChange={(e) => updateField('adminName', e.target.value)}
                                    />
                                </div>
                            </div>
                            <div className="space-y-2">
                                <Label>Email Maestro *</Label>
                                <div className="relative">
                                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                    <Input
                                        type="email"
                                        placeholder="admin@ejemplo.com"
                                        className="pl-10"
                                        value={formData.adminEmail}
                                        onChange={(e) => updateField('adminEmail', e.target.value)}
                                    />
                                </div>
                                <p className="text-[10px] text-muted-foreground">Este será el correo para iniciar sesión.</p>
                            </div>
                            <div className="space-y-2">
                                <Label>Contraseña Temporal *</Label>
                                <div className="relative">
                                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                    <Input
                                        type="password"
                                        placeholder="Mínimo 6 caracteres"
                                        className="pl-10"
                                        value={formData.adminPassword}
                                        onChange={(e) => updateField('adminPassword', e.target.value)}
                                    />
                                </div>
                            </div>
                            <div className="flex items-center gap-2 pt-2">
                                <Switch
                                    checked={formData.sendEmail}
                                    onCheckedChange={(v) => updateField('sendEmail', v)}
                                />
                                <Label>¿Enviar credenciales por email al completar?</Label>
                            </div>
                        </div>
                    </div>
                )}

                {currentStep === 3 && (
                    <div className="space-y-6 animate-in fade-in slide-in-from-right-4">
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                            {saasTiers
                                .map((tier) => (
                                    <div
                                        key={tier.id}
                                        onClick={() => {
                                            updateField('plan', tier.name);
                                            updateField('tier_id', tier.id);
                                            updateField('price', tier.price.toString());
                                            if (tier.included_modules) {
                                                updateField('modules', tier.included_modules);
                                            }
                                        }}
                                        className={cn(
                                            "p-4 border-2 rounded-xl cursor-pointer transition-all text-center space-y-2 relative overflow-hidden",
                                            formData.tier_id === tier.id ? "border-primary bg-primary/5 shadow-md" : "border-muted hover:border-muted-foreground/30"
                                        )}
                                    >
                                        {tier.is_private && (
                                            <div className="absolute top-0 left-0">
                                                <Badge variant="outline" className="h-3 text-[7px] px-1 border-amber-200 text-amber-600 rounded-none rounded-br-lg bg-amber-50/50">
                                                    PRIVADO
                                                </Badge>
                                            </div>
                                        )}
                                        <div className={cn(
                                            "w-8 h-8 rounded-full mx-auto flex items-center justify-center mb-1",
                                            formData.tier_id === tier.id ? "bg-primary text-white" : "bg-muted text-muted-foreground"
                                        )}>
                                            <ShieldCheck className="h-4 w-4" />
                                        </div>
                                        <p className="font-bold text-xs uppercase truncate px-2">{tier.name}</p>
                                        <p className="text-lg font-black text-primary">${tier.price}</p>
                                        <p className="text-[9px] text-muted-foreground line-clamp-2">
                                            Incluye {tier.included_modules.length} módulos
                                        </p>
                                        {formData.tier_id === tier.id && (
                                            <div className="absolute top-0 right-0 p-1">
                                                <Badge variant="success" className="h-4 w-4 p-0 flex items-center justify-center rounded-full">
                                                    <Check className="h-2 w-2" />
                                                </Badge>
                                            </div>
                                        )}
                                    </div>
                                ))}
                        </div>

                        <div className="grid grid-cols-2 gap-4 pt-4">
                            <div className="space-y-2">
                                <Label>Precio personalizado ($)</Label>
                                <Input
                                    type="number"
                                    value={formData.price}
                                    onChange={(e) => updateField('price', e.target.value)}
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Ciclo de Facturación</Label>
                                <Select value={formData.billingCycle} onValueChange={(v) => updateField('billingCycle', v)}>
                                    <SelectTrigger>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="monthly">Mensual</SelectItem>
                                        <SelectItem value="annual">Anual (20% descuento)</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>

                        {formData.billingCycle === 'annual' && (
                            <div className="p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-3">
                                <Badge className="bg-green-600 text-white font-black">-20%</Badge>
                                <div className="flex-1 text-sm">
                                    <p className="font-bold text-green-900">Pago anual con descuento aplicado</p>
                                    <p className="text-green-700 text-xs">
                                        Precio mensual: ${formData.price || '0'} · Total anual: ${(parseFloat(formData.price || '0') * 12 * 0.8).toFixed(2)}
                                        {' '}(ahorro de ${(parseFloat(formData.price || '0') * 12 * 0.2).toFixed(2)} vs. pago mensual).
                                    </p>
                                </div>
                            </div>
                        )}

                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>Estado Inicial</Label>
                                <Select value={formData.status} onValueChange={(v) => updateField('status', v)}>
                                    <SelectTrigger>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="active">Activo</SelectItem>
                                        <SelectItem value="pending">En Prueba</SelectItem>
                                        <SelectItem value="inactive">Inactivo</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-2">
                                <Label>Fecha de Vencimiento</Label>
                                <Input
                                    type="date"
                                    value={formData.expiryDate}
                                    onChange={(e) => updateField('expiryDate', e.target.value)}
                                />
                            </div>
                        </div>

                        <div className="flex items-center gap-3 pt-2">
                            <Switch
                                checked={formData.isTrial}
                                onCheckedChange={(v) => updateField('isTrial', v)}
                            />
                            <div>
                                <Label htmlFor="is-trial">Modo prueba (trial)</Label>
                                <p className="text-[10px] text-muted-foreground">
                                    Nuevo trial por defecto: plan Elite (todo excepto WhatsApp). Aplica límites de 10 clientes y 20 productos de inventario.
                                </p>
                            </div>
                        </div>
                    </div>
                )}

                {currentStep === 4 && (
                    <div className="space-y-4 animate-in fade-in slide-in-from-right-4">
                        <p className="text-sm font-medium mb-4">Módulos habilitados (según el plan seleccionado en el paso anterior):</p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {ALL_MODULES.map((mod) => {
                                const isSelected = formData.modules.includes(mod.module_id);
                                const Icon = (Icons as any)[mod.icon] || Icons.Circle;
                                return (
                                    <div
                                        key={mod.module_id}
                                        onClick={() => {
                                            if (isSelected) {
                                                updateField('modules', formData.modules.filter(m => m !== mod.module_id));
                                            } else {
                                                updateField('modules', [...formData.modules, mod.module_id]);
                                            }
                                        }}
                                        className={cn(
                                            "flex items-center p-3 border rounded-lg cursor-pointer transition-colors relative",
                                            isSelected ? "border-primary bg-primary/5" : "hover:bg-muted/50"
                                        )}
                                    >
                                        <div className={cn(
                                            "w-8 h-8 rounded-lg flex items-center justify-center mr-3",
                                            isSelected ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"
                                        )}>
                                            <Icon className="h-4 w-4" />
                                        </div>
                                        <div className="flex-1">
                                            <div className="flex justify-between items-center pr-6">
                                                <p className="text-sm font-bold">{mod.name}</p>
                                                <span className="text-[10px] font-bold text-primary">
                                                    {mod.price > 0 ? `+$${mod.price}` : 'Gratis'}
                                                </span>
                                            </div>
                                            <p className="text-[10px] text-muted-foreground line-clamp-1">{mod.description}</p>
                                        </div>
                                        <div className={cn(
                                            "absolute top-2 right-2 w-4 h-4 rounded-full border flex items-center justify-center",
                                            isSelected ? "bg-primary border-primary text-white" : "border-muted-foreground/30"
                                        )}>
                                            {isSelected && <Icons.Check className="h-2 w-2" />}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                {currentStep === 5 && (
                    <div className="space-y-6 animate-in fade-in slide-in-from-right-4">
                        <div className="grid grid-cols-2 gap-6">
                            <div className="space-y-4">
                                <Label className="flex items-center gap-2">
                                    <Clock className="h-4 w-4" /> Horario Comercial
                                </Label>
                                <div className="flex items-center gap-2">
                                    <Input
                                        type="time"
                                        value={formData.openingHours}
                                        onChange={(e) => updateField('openingHours', e.target.value)}
                                    />
                                    <span>a</span>
                                    <Input
                                        type="time"
                                        value={formData.closingHours}
                                        onChange={(e) => updateField('closingHours', e.target.value)}
                                    />
                                </div>
                            </div>
                            <div className="space-y-4">
                                <Label>Método de Asistencia</Label>
                                <Select value={formData.attendanceMethod} onValueChange={(v) => updateField('attendanceMethod', v)}>
                                    <SelectTrigger>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="qr">Escaneo QR (App/Scanner)</SelectItem>
                                        <SelectItem value="code">Código Numérico</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-6 pt-4 border-t">
                            <div className="space-y-4">
                                <Label>Capacidad Máxima Simultánea</Label>
                                <Input
                                    type="number"
                                    value={formData.maxCapacity}
                                    onChange={(e) => updateField('maxCapacity', e.target.value)}
                                />
                            </div>
                            <div className="flex flex-col space-y-4">
                                <Label>Opciones Adicionales</Label>
                                <div className="flex items-center gap-2 mt-2">
                                    <Switch
                                        checked={formData.multipleCheckin}
                                        onCheckedChange={(v) => updateField('multipleCheckin', v)}
                                    />
                                    <span className="text-xs">Permitir entrada recurrente el mismo día</span>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {currentStep === 6 && (
                    <div className="space-y-6 animate-in fade-in slide-in-from-right-4 max-w-md mx-auto">
                        <div className="text-center space-y-2 mb-6">
                            <div className="w-16 h-16 bg-green-500/10 text-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
                                <CheckCircle2 className="h-10 w-10" />
                            </div>
                            <h3 className="text-xl font-bold">¡Todo listo!</h3>
                            <p className="text-sm text-muted-foreground">Verifica los datos antes de crear el entorno.</p>
                        </div>

                        <div className="space-y-3 bg-muted/50 p-4 rounded-xl border">
                            <div className="flex justify-between items-center text-sm">
                                <span className="text-muted-foreground">Gimnasio:</span>
                                <span className="font-bold">{formData.name}</span>
                            </div>
                            <div className="flex justify-between items-center text-sm">
                                <span className="text-muted-foreground">Plan:</span>
                                <Badge variant="info" className="uppercase">{formData.plan}</Badge>
                            </div>
                            <div className="flex justify-between items-center text-sm">
                                <span className="text-muted-foreground">Modo:</span>
                                <Badge variant={formData.isTrial ? 'warning' : 'success'} className="uppercase">
                                    {formData.isTrial ? 'Trial (30 días)' : 'Pagado'}
                                </Badge>
                            </div>
                            <div className="flex justify-between items-center text-sm">
                                <span className="text-muted-foreground">Facturación:</span>
                                <span className="font-medium text-primary">
                                    ${calculateTotalPrice()} / {formData.billingCycle === 'monthly' ? 'mes' : 'año'}
                                </span>
                            </div>
                            <div className="flex justify-between items-center text-xs">
                                <span className="text-muted-foreground pl-2 text-[10px]">
                                    (Base ${selectedTier?.price ?? 0} + Addons ${(calculateTotalPrice() - (selectedTier?.price ?? 0)).toFixed(2)})
                                </span>
                            </div>
                            <div className="flex justify-between items-center text-sm">
                                <span className="text-muted-foreground">Administrador:</span>
                                <span className="font-medium">{formData.adminEmail}</span>
                            </div>
                            <div className="pt-2 border-t mt-2">
                                <p className="text-[10px] uppercase text-muted-foreground font-bold mb-1">Módulos habilitados:</p>
                                <div className="flex flex-wrap gap-1">
                                    {formData.modules.map(m => (
                                        <Badge key={m} variant="secondary" className="text-[9px] h-4">
                                            {ALL_MODULES.find(o => o.module_id === m)?.name}
                                        </Badge>
                                    ))}
                                </div>
                            </div>
                        </div>

                        <div className="bg-yellow-500/10 border border-yellow-500/20 p-3 rounded-lg text-xs text-yellow-700 flex gap-2">
                            <ShieldCheck className="h-4 w-4 shrink-0" />
                            Al confirmar, se creará el tenant y se enviará la contraseña temporal si fue solicitado.
                        </div>
                    </div>
                )}
            </div>

            {/* Footer Buttons */}
            <div className="flex justify-between mt-8 pt-4 border-t">
                {currentStep > 1 ? (
                    <Button variant="ghost" onClick={handleBack} disabled={isSubmitting}>
                        <ArrowLeft className="h-4 w-4 mr-2" />
                        Atrás
                    </Button>
                ) : (
                    <Button variant="ghost" onClick={onCancel} disabled={isSubmitting}>
                        Cancelar
                    </Button>
                )}

                {currentStep < 6 ? (
                    <Button onClick={handleNext} disabled={!isStepValid()} variant="gradient">
                        Siguiente
                        <ArrowRight className="h-4 w-4 ml-2" />
                    </Button>
                ) : (
                    <Button onClick={handleSubmit} disabled={isSubmitting} variant="gradient" className="px-8 h-11 font-bold">
                        {isSubmitting ? (
                            <>
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                Creando Entorno...
                            </>
                        ) : (
                            'Confirmar y Crear Gimnasio'
                        )}
                    </Button>
                )}
            </div>
        </div>
    );
}
